import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  Download,
  Eye,
  FileCheck,
  FileText,
  Pencil,
  Trash2,
  Undo2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { useCompany } from '@/features/crm/companies/queries';
import { CustomFieldsPanel } from '@/features/custom-fields';
import { formatHoursMinutes, HOUR_UNITS } from '@/lib/hours';
import { formatMoney } from '@/lib/money';
import { useWorklogSummary } from '@/features/projects/queries';
import { formatMinutes } from '@/features/projects/timeFormat';
import { downloadInvoice, getInvoiceBlob } from '../api';
import { AddInvoiceLineForm } from '../components/AddInvoiceLineForm';
import { directionLabel, invoiceStatusLabel, invoiceStatusVariant } from '../constants';
import {
  useDeleteInvoice,
  useGenerateInvoicePdf,
  useInvoice,
  useIssueInvoice,
  useMarkInvoicePaid,
  useMoveInvoiceLine,
  useRemoveInvoiceLine,
  useRemoveInvoicePdfInsert,
  useRevertInvoiceToDraft,
  useUpdateInvoiceLine,
  useInvoiceBillingOverlap,
  useUploadInvoicePdfInserts,
} from '../queries';
import type { InvoiceLine, InvoicePdfInsert } from '../types';

/** Money with the short "$" symbol (not "US$"). */
const money = (v: string | null | undefined, c: string | null | undefined) =>
  formatMoney(v, c, { narrowSymbol: true });

/** The "/ x" suffix shown after the unit rate: short "h" for hours, the raw
 *  label for quantity lines (e.g. "month"), nothing for unitless lines. */
const rateUnitSuffix = (unit: string | null): string =>
  HOUR_UNITS.has(unit ?? '') ? 'h' : (unit ?? '');

interface LineRowProps {
  line: InvoiceLine;
  /** When true the description is inline-editable and row actions show. */
  editable: boolean;
  onSave: (lineId: number, description: string) => Promise<unknown>;
  onRemove: (lineId: number) => void;
  onMove: (lineId: number, direction: 'up' | 'down') => void;
  savingId: number | null;
  removingId: number | null;
  /** Reorder-arrow enablement + in-flight state. */
  canMoveUp: boolean;
  canMoveDown: boolean;
  isMoving: boolean;
}

/** One line-item row. In draft mode the description is edited inline (click →
 * input with save/cancel) and a trailing trash action removes the line. */
function LineRow({
  line,
  editable,
  onSave,
  onRemove,
  onMove,
  savingId,
  removingId,
  canMoveUp,
  canMoveDown,
  isMoving,
}: LineRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(line.description);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSaving = savingId === line.id;
  const isRemoving = removingId === line.id;

  // Focus the field when edit mode opens (autoFocus is disallowed by a11y lint).
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function cancel() {
    setDraft(line.description);
    setEditing(false);
  }

  async function save() {
    await onSave(line.id, draft.trim());
    setEditing(false);
  }

  return (
    <TableRow className={isRemoving ? 'opacity-50' : undefined}>
      <TableCell>
        {editing ? (
          <div className="flex items-center gap-1.5">
            <Input
              ref={inputRef}
              value={draft}
              className="h-8"
              disabled={isSaving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void save();
                } else if (e.key === 'Escape') {
                  cancel();
                }
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              aria-label="Save description"
              disabled={isSaving}
              onClick={() => void save()}
            >
              <Check className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              aria-label="Cancel edit"
              disabled={isSaving}
              onClick={cancel}
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : editable ? (
          <button
            type="button"
            className="group hover:text-primary inline-flex items-center gap-1.5 text-left"
            onClick={() => {
              setDraft(line.description);
              setEditing(true);
            }}
          >
            <span>{line.description || '—'}</span>
            <Pencil className="size-3.5 opacity-0 transition-opacity group-hover:opacity-60" />
          </button>
        ) : (
          line.description || '—'
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {HOUR_UNITS.has(line.unit ?? '')
          ? formatHoursMinutes(line.quantity)
          : Number(line.quantity).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {money(line.unit_rate, line.currency) ?? '—'}
        {rateUnitSuffix(line.unit) ? (
          <span className="text-muted-foreground"> / {rateUnitSuffix(line.unit)}</span>
        ) : null}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {money(line.line_total, line.currency) ?? '—'}
      </TableCell>
      <TableCell className="hidden text-right tabular-nums sm:table-cell">
        {money(line.base_total, 'USD') ?? '—'}
      </TableCell>
      {editable ? (
        <TableCell className="text-right">
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground size-8"
              aria-label="Move line up"
              disabled={!canMoveUp || isMoving}
              onClick={() => onMove(line.id, 'up')}
            >
              <ChevronUp className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground size-8"
              aria-label="Move line down"
              disabled={!canMoveDown || isMoving}
              onClick={() => onMove(line.id, 'down')}
            >
              <ChevronDown className="size-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive size-8"
                  aria-label="Remove line"
                  disabled={isRemoving}
                >
                  <Trash2 className="size-4" />
                </Button>
              </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove this line?</AlertDialogTitle>
                <AlertDialogDescription>
                  “{line.description || 'This line'}” will be removed and the invoice total
                  recalculated.
                </AlertDialogDescription>
              </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onRemove(line.id)}>Remove</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      ) : null}
    </TableRow>
  );
}

/** Compact preview of the worklogs that attach to the invoice PDF, grouped per
 * employee (and per project when there are several) — mirrors
 * `services.invoice_worklogs` (DEV13-77, BE ADR-101): once a client invoice is
 * issued/paid, exactly what this invoice billed; before that, the linked
 * projects' billable entries in the period that no other invoice has billed
 * (with a warning naming those invoices). */
function InvoiceWorklogsSection({
  invoiceId,
  publicId,
  billed,
  projectIds,
  from,
  to,
}: {
  invoiceId: number;
  publicId: string;
  /** A client invoice that is issued or paid — its worklogs are recorded as billed. */
  billed: boolean;
  projectIds: number[];
  from: string | null;
  to: string | null;
}) {
  const hasPeriod = Boolean(from && to);
  // Server-side aggregate over the FULL filtered set — the worklogs list is
  // paginated, so grouping a page of entries here undercounted. `users` gives
  // the per-employee rows, `groups` the per-project split.
  const { data, isPending } = useWorklogSummary(
    billed
      ? { billed_by: invoiceId, group_by: 'project' }
      : {
          projects: projectIds.join(','),
          billable: true,
          billed: false,
          date_from: from ?? undefined,
          date_to: to ?? undefined,
          group_by: 'project',
        },
  );
  const { data: overlap, isError: overlapFailed } = useInvoiceBillingOverlap(
    publicId,
    { projects: projectIds, from, to },
    !billed && hasPeriod,
  );
  const byProject = data?.group_by === 'project' ? data.groups : [];
  const users = data?.users ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="size-4" /> Worklogs
          <InfoHint
            text={
              hasPeriod
                ? 'Billable timesheets for the linked projects over the invoice period are appended to the generated PDF — one page per project and employee. Non-billable time, and time another invoice already billed, is never included. Once issued, these worklogs are locked until the invoice is reverted to draft — a paid invoice locks them for good.'
                : 'Set a service period on the invoice to attach the linked projects’ billable worklogs to the PDF.'
            }
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {overlap && overlap.entries > 0 ? (
          <p role="status" className="text-sm text-amber-600 dark:text-amber-400">
            {formatMinutes(overlap.minutes)} in this period is already billed by{' '}
            {overlap.invoices.map((i) => i.number || 'another invoice').join(', ')} — not
            included here.
          </p>
        ) : overlapFailed ? (
          <p className="text-muted-foreground text-sm">
            {"Couldn't check other invoices for time already billed in this period."}
          </p>
        ) : null}
        {isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : users.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No billable worklogs {hasPeriod ? 'in this period' : 'yet'}.
          </p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Entries</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user ?? 'unassigned'}>
                    <TableCell>{u.user_name ?? 'Unassigned'}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.entries}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMinutes(u.minutes)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t font-medium">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {data?.total_entries ?? 0}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMinutes(data?.total_minutes ?? 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
        {!isPending && byProject.length > 1 ? (
          <ul aria-label="Hours by project" className="text-muted-foreground space-y-0.5 text-sm">
            {byProject.map((g) => (
              <li key={String(g.key)} className="flex justify-between gap-4">
                <span className="truncate">{g.label}</span>
                <span className="tabular-nums">{formatMinutes(g.minutes)}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

const fmtBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Upload extra PDFs (e.g. external Everhour timesheets) that merge after the
 * invoice + worklog pages. A merge source only — kept out of Attachments. */
function InvoicePdfInsertsSection({
  invoiceId,
  inserts,
  editable,
}: {
  invoiceId: string;
  inserts: InvoicePdfInsert[];
  editable: boolean;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const upload = useUploadInvoicePdfInserts(invoiceId);
  const remove = useRemoveInvoicePdfInsert(invoiceId);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length) upload.mutate(files);
    e.target.value = ''; // allow re-picking the same file
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="size-4" /> Attached pages
          <InfoHint text="Extra PDFs (e.g. an external timesheet export) are appended after the invoice when the PDF is generated. PDF only." />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {inserts.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableBody>
                {inserts.map((ins) => (
                  <TableRow key={ins.id}>
                    <TableCell className="font-medium">{ins.name}</TableCell>
                    <TableCell className="text-muted-foreground text-right tabular-nums">
                      {fmtBytes(ins.size)}
                    </TableCell>
                    {editable ? (
                      <TableCell className="w-10 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive size-8"
                          aria-label={`Remove ${ins.name}`}
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(ins.id)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
        {editable ? (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={onPick}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInput.current?.click()}
              disabled={upload.isPending}
            >
              <Upload className="size-4" /> {upload.isPending ? 'Uploading…' : 'Add PDF'}
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function InvoiceDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(false);
  const { data: invoice, isPending, isError, error } = useInvoice(publicId);
  // Auth-fetch the PDF (JWT/org headers) — a raw media `src`/link 401s / fails CORS.
  const fetchPdf = useCallback(() => getInvoiceBlob(publicId), [publicId]);
  const company = useCompany(invoice?.company ?? -1);
  const del = useDeleteInvoice();
  const issue = useIssueInvoice(invoice?.id ?? -1);
  const markPaid = useMarkInvoicePaid(invoice?.id ?? -1);
  const revertToDraft = useRevertInvoiceToDraft(publicId);
  const generatePdf = useGenerateInvoicePdf(invoice?.id ?? -1);
  const updateLine = useUpdateInvoiceLine(publicId);
  const removeLine = useRemoveInvoiceLine(publicId);
  const moveLine = useMoveInvoiceLine(publicId);

  async function handleDelete() {
    if (!invoice) return;
    await del.mutateAsync(invoice.id);
    navigate('/finance/billing');
  }

  // A draft PDF is regenerated on demand (it can still change). Once issued the
  // invoice is frozen — view the snapshot taken at issue time, never regenerate.
  // The `!document` fallback only covers a legacy issued invoice with no snapshot.
  async function ensureFreshPdf() {
    if (invoice && (invoice.status === 'draft' || !invoice.document)) {
      await generatePdf.mutateAsync();
    }
  }
  async function viewPdf() {
    await ensureFreshPdf();
    setPreview(true);
  }
  async function downloadPdf() {
    if (!invoice) return;
    await ensureFreshPdf();
    await downloadInvoice(publicId, `${invoice.number}.pdf`);
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/finance/billing')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
  const isDraft = invoice.status === 'draft';
  // A paid invoice is a committed record — lock it down (no re-generate / edit / delete).
  const isPaid = invoice.status === 'paid';
  // Which line (if any) is mid-mutation — drives per-row pending state.
  const savingLineId = updateLine.isPending ? (updateLine.variables?.lineId ?? null) : null;
  const removingLineId = removeLine.isPending ? (removeLine.variables ?? null) : null;
  const movingLineId = moveLine.isPending ? (moveLine.variables?.lineId ?? null) : null;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/finance/billing')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{invoice.number}</h1>
          <Badge variant="outline">{directionLabel(invoice.direction)}</Badge>
          <Badge variant={invoiceStatusVariant(invoice.status)}>
            {invoiceStatusLabel(invoice.status)}
          </Badge>
        </div>
        <div className="flex gap-2">
          {isDraft ? (
            <Button onClick={() => issue.mutate()} disabled={issue.isPending}>
              <FileCheck /> {issue.isPending ? 'Issuing…' : 'Issue'}
            </Button>
          ) : null}
          {invoice.status === 'issued' ? (
            <>
              <Button onClick={() => markPaid.mutate()} disabled={markPaid.isPending}>
                <CircleDollarSign /> {markPaid.isPending ? 'Marking…' : 'Mark paid'}
              </Button>
              <Button
                variant="outline"
                onClick={() => revertToDraft.mutate()}
                disabled={revertToDraft.isPending}
              >
                <Undo2 /> {revertToDraft.isPending ? 'Reverting…' : 'Revert to draft'}
              </Button>
            </>
          ) : null}
          {invoice.status !== 'void' ? (
            <>
              <Button
                variant="outline"
                onClick={() => void viewPdf()}
                disabled={generatePdf.isPending}
              >
                {isDraft ? <FileText /> : <Eye />}{' '}
                {generatePdf.isPending
                  ? isDraft
                    ? 'Generating…'
                    : 'Preparing…'
                  : isDraft
                    ? 'Generate PDF'
                    : 'View PDF'}
              </Button>
              <Button
                variant="outline"
                onClick={() => void downloadPdf()}
                disabled={generatePdf.isPending}
              >
                <Download /> Download PDF
              </Button>
            </>
          ) : null}
          {isDraft ? (
            <Button
              variant="outline"
              onClick={() => navigate(`/finance/billing/${invoice.public_id}/edit`)}
            >
              <Pencil /> Edit
            </Button>
          ) : null}
          {!isPaid ? (
            <>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {invoice.number}?</AlertDialogTitle>
                    <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} disabled={del.isPending}>
                      {del.isPending ? 'Deleting…' : 'Delete'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <div>
            <p className="text-xs font-medium uppercase">Counterparty</p>
            <p className="text-foreground mt-1">
              {invoice.company ? (company.data?.name ?? '…') : invoice.counterparty_name || '—'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase">Subtotal ({invoice.currency})</p>
            <p className="text-foreground mt-1 tabular-nums">
              {money(invoice.subtotal, invoice.currency) ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase">Total (USD base)</p>
            <p className="text-foreground mt-1 font-semibold tabular-nums">
              {money(invoice.base_total, 'USD') ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase">Issued</p>
            <p className="text-foreground mt-1">{fmtDate(invoice.issue_date)}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase">Due</p>
            <p className="text-foreground mt-1">{fmtDate(invoice.due_date)}</p>
          </div>
          {invoice.period_start || invoice.period_end ? (
            <div>
              <p className="text-xs font-medium uppercase">Period</p>
              <p className="text-foreground mt-1">
                {fmtDate(invoice.period_start)} – {fmtDate(invoice.period_end)}
              </p>
            </div>
          ) : null}
          {invoice.project_details.length > 0 ? (
            <div>
              <p className="text-xs font-medium uppercase">
                {invoice.project_details.length > 1 ? 'Projects' : 'Project'}
              </p>
              <p className="text-foreground mt-1">
                {invoice.project_details.map((p) => p.name).join(', ')}
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {invoice.projects.length > 0 ? (
        <InvoiceWorklogsSection
          invoiceId={invoice.id}
          publicId={invoice.public_id}
          billed={
            invoice.direction === 'receivable' &&
            (invoice.status === 'issued' || invoice.status === 'paid')
          }
          projectIds={invoice.projects}
          from={invoice.period_start}
          to={invoice.period_end}
        />
      ) : null}

      {isDraft || invoice.pdf_inserts.length > 0 ? (
        <InvoicePdfInsertsSection
          invoiceId={publicId}
          inserts={invoice.pdf_inserts}
          editable={isDraft}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Line items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit rate</TableHead>
                  <TableHead className="text-right">Line total</TableHead>
                  <TableHead className="hidden text-right sm:table-cell">Base (USD)</TableHead>
                  {isDraft ? <TableHead className="w-10" aria-label="Actions" /> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.lines.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={isDraft ? 6 : 5}
                      className="text-muted-foreground py-8 text-center"
                    >
                      No line items yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  invoice.lines.map((line, i) => (
                    <LineRow
                      key={line.id}
                      line={line}
                      editable={isDraft}
                      savingId={savingLineId}
                      removingId={removingLineId}
                      canMoveUp={i > 0}
                      canMoveDown={i < invoice.lines.length - 1}
                      isMoving={movingLineId === line.id}
                      onSave={(lineId, description) =>
                        updateLine.mutateAsync({ lineId, input: { description } })
                      }
                      onRemove={(lineId) => removeLine.mutate(lineId)}
                      onMove={(lineId, direction) => moveLine.mutate({ lineId, direction })}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {!isDraft ? (
            <p className="text-muted-foreground mt-3 text-sm">
              This invoice is issued — it&rsquo;s locked as an immutable snapshot. To change it,
              use <span className="font-medium">Revert to draft</span>, edit, then issue it again.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {isDraft ? (
        <Card>
          <CardHeader>
            <CardTitle>Add a line</CardTitle>
          </CardHeader>
          <CardContent>
            <AddInvoiceLineForm invoiceId={invoice.id} defaultCurrency={invoice.currency} />
          </CardContent>
        </Card>
      ) : null}

      <CustomFieldsPanel entity="finance.invoice" entityId={invoice.id} />

      <PdfPreviewDialog
        open={preview}
        onOpenChange={setPreview}
        title={`${invoice.number} — PDF`}
        fetchBlob={invoice.document ? fetchPdf : undefined}
      />
    </div>
  );
}
